import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { dataService } from '@/services/data-service';
import { useMode } from '@/providers/ModeProvider';
import { useRegisterPageRefresh } from '@/contexts/PageRefreshContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Download, FileSpreadsheet, FileText, TrendingUp } from 'lucide-react';
import { exportToCSV, exportToExcel, exportToPDF, generateFilename, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { toast } from 'sonner';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Statutory Report Data Structure
interface StatutoryReportRow {
  label: string;
  companyTotal: number;
  category1?: number;
  category2?: number;
  category3?: number;
  category4?: number;
  category5?: number;
  category6?: number;
  category7?: number;
  category8?: number;
  category9?: number;
  category10?: number;
  isHeader?: boolean;
  isBold?: boolean;
}

interface StatutoryReportData {
  year: number;
  period: number;
  categoryLabels: string[];
  rows: StatutoryReportRow[];
}

// API response row type
interface ApiReportRow {
  label: string;
  companyTotal: number;
  categoryValues: Record<string, number>;
  isHeader?: boolean;
  isBold?: boolean;
}

export function StatutoryReport(): React.ReactElement {
  const { mode } = useMode();
  const [data, setData] = useState<StatutoryReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Year and Period selection
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedPeriod, setSelectedPeriod] = useState(12);

  // Generate year options (current year and 5 years back)
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);
  const periodOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // Load data function
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call the API to get statutory report data
      const apiData = await dataService.getStatutoryReport(selectedYear, selectedPeriod);
      
      // Transform API response to match the expected format
      // The API returns categoryValues as a dictionary, but the UI expects category1, category2, etc.
      const transformedRows: StatutoryReportRow[] = apiData.rows.map((row: ApiReportRow) => {
        const transformedRow: StatutoryReportRow = {
          label: row.label,
          companyTotal: row.companyTotal,
          isHeader: row.isHeader,
          isBold: row.isBold,
        };
        
        // Map categoryValues to category1, category2, etc.
        apiData.categoryLabels.forEach((label: string, index: number) => {
          const key = `category${index + 1}` as keyof StatutoryReportRow;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (transformedRow as any)[key] = row.categoryValues[label] ?? 0;
        });
        
        return transformedRow;
      });
      
      setData({
        year: apiData.year,
        period: apiData.period,
        categoryLabels: apiData.categoryLabels,
        rows: transformedRows,
      });
    } catch (err) {
      console.error('Failed to load statutory report data:', err);
      setError('Failed to load statutory report data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedPeriod]);

  // Load data on mount and when year/period changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Register refresh handler for breadcrumb
  useRegisterPageRefresh(loadData, '/reports/statutory');

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleExportCSV = () => {
    if (!data) return;

    try {
      const columns: ExportColumn[] = [
        { key: 'label', label: '' },
        { key: 'companyTotal', label: 'Company Total', format: formatCurrencyForExport },
      ];

      // Add category columns
      data.categoryLabels.forEach((label, index) => {
        columns.push({
          key: `category${index + 1}`,
          label,
          format: formatCurrencyForExport,
        });
      });

      exportToCSV({
        filename: generateFilename(`statutory-report-${data.year}-P${data.period}`, 'csv'),
        columns,
        data: data.rows,
      });

      toast.success('Exported statutory report to CSV');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportExcel = () => {
    if (!data) return;

    try {
      const columns: ExportColumn[] = [
        { key: 'label', label: '' },
        { key: 'companyTotal', label: 'Company Total', format: formatCurrencyForExport },
      ];

      // Add category columns
      data.categoryLabels.forEach((label, index) => {
        columns.push({
          key: `category${index + 1}`,
          label,
          format: formatCurrencyForExport,
        });
      });

      exportToExcel({
        filename: generateFilename(`statutory-report-${data.year}-P${data.period}`, 'xls'),
        sheetName: 'Statutory Report',
        columns,
        data: data.rows,
      });

      toast.success('Exported statutory report to Excel');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportPDF = () => {
    if (!data) return;

    try {
      const columns: ExportColumn[] = [
        { key: 'label', label: '' },
        { key: 'companyTotal', label: 'Company Total', format: formatCurrencyForExport },
      ];

      // Add category columns
      data.categoryLabels.forEach((label, index) => {
        columns.push({
          key: `category${index + 1}`,
          label,
          format: formatCurrencyForExport,
        });
      });

      exportToPDF({
        filename: generateFilename(`statutory-report-${data.year}-P${data.period}`, 'pdf'),
        title: 'Fixed Assets Statutory Report',
        subtitle: `Year: ${data.year} | Period: ${data.period}`,
        columns,
        data: data.rows.filter(row => !row.isHeader),
        orientation: 'landscape',
      });

      toast.success('Exported statutory report to PDF');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Page Header */}
      <div className="border-b bg-card p-6 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Fixed Assets Statutory Report</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Comprehensive statutory report for financial statement disclosure
              </p>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-3">
            <EvoFeatureFeedbackCollector feature="Statutory Report" />
            
            {/* Year Selection */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Year:</label>
              <Select value={selectedYear.toString()} onValueChange={(value: string) => setSelectedYear(parseInt(value))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Period Selection */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Period:</label>
              <Select value={selectedPeriod.toString()} onValueChange={(value: string) => setSelectedPeriod(parseInt(value))}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {periodOptions.map((period) => (
                    <SelectItem key={period} value={period.toString()}>
                      {period}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Export Buttons */}
            <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={!data}>
              <Download className="h-4 w-4 mr-2" />
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={!data}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={!data}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {loading && (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-lg font-medium">Loading statutory report...</p>
            </div>
          </div>
        )}
        {!loading && error && (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-lg font-medium text-destructive">Error</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
        )}
        {!loading && !error && data && (
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                <TableRow className="border-b-2 border-slate-200 dark:border-slate-700">
                  <TableHead className="h-12 font-semibold w-64">
                    {data.year} - Period {data.period}
                  </TableHead>
                  <TableHead className="h-12 font-semibold text-right w-40">
                    Company Total
                  </TableHead>
                  {data.categoryLabels.map((label) => (
                    <TableHead key={label} className="h-12 font-semibold text-right w-40">
                      {label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((row) => {
                  if (row.isHeader) {
                    return (
                      <TableRow key={row.label} className="bg-slate-100 dark:bg-slate-800 border-t-2 border-slate-300 dark:border-slate-600">
                        <TableCell colSpan={2 + data.categoryLabels.length} className="py-3 px-4 font-bold text-base">
                          {row.label}
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow 
                      key={row.label}
                      className={`
                        bg-white dark:bg-slate-950
                        hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
                      `}
                    >
                      <TableCell className={`px-4 py-3 ${row.isBold ? 'font-semibold' : ''}`}>
                        {row.label}
                      </TableCell>
                      <TableCell className={`px-4 py-3 text-right tabular-nums ${row.isBold ? 'font-semibold' : ''}`}>
                        {row.companyTotal !== 0 ? formatCurrency(row.companyTotal) : '-'}
                      </TableCell>
                      {data.categoryLabels.map((_, catIndex) => {
                        const value = (row as any)[`category${catIndex + 1}`];
                        return (
                          <TableCell key={catIndex} className={`px-4 py-3 text-right tabular-nums ${row.isBold ? 'font-semibold' : ''}`}>
                            {value !== undefined && value !== 0 ? formatCurrency(value) : '-'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Footer with Summary */}
      {!loading && !error && data && (
        <div className="border-t bg-card px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>
              Report Period: {data.year} - Period {data.period}
            </div>
            <div className="flex gap-6">
              <span>
                Total Cost: {formatCurrency(data.rows.find(r => r.label === 'End of Year Cost')?.companyTotal || 0)}
              </span>
              <span>
                Total Depreciation: {formatCurrency(data.rows.find(r => r.label === 'Depreciation End of Year Cost')?.companyTotal || 0)}
              </span>
              <span className="font-semibold">
                Net Book Value: {formatCurrency(data.rows.find(r => r.label === 'End of Year Net Book Value')?.companyTotal || 0)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatutoryReport;
